import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, MessageSquareQuote, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHero, Section, Button } from "@/components/UI";
import { Reveal, Overline } from "@/components/Reveal";

const AREAS = [
  "Professional standards and ethics",
  "Minimum education and competency requirements",
  "Certification and continuing education",
  "Infection prevention and client safety",
  "Terminology and scope of practice",
  "Collaboration with medical professionals",
  "Referral pathways",
  "Insurance and accessibility",
  "Public education",
  "Practitioner accountability",
  "The potential role of a national professional body",
];

const STAKEHOLDERS = [
  "Practitioner", "Educator", "Physician / Surgeon", "Nurse", "Cancer-care professional",
  "Public health representative", "Insurer", "Legal / Regulatory professional",
  "Patient advocate", "Breast cancer survivor", "Other interested stakeholder",
];

const LETTER = [
  "Paramedical micropigmentation is a growing and highly specialized field that sits at the intersection of artistry, restorative care, skin science, and post-surgical recovery. As awareness of services such as 3D areola restoration, scar camouflage, and other restorative tattooing techniques continues to grow, so does the need for greater clarity, consistency, education, and professional recognition.",
  "At present, practitioners across Canada may enter the field through very different educational pathways, follow varying standards of practice, and work under regulations that were not specifically designed to address the unique nature of paramedical micropigmentation. Healthcare professionals, insurers, municipalities, public health authorities, and clients may also have limited guidance when trying to understand practitioner qualifications, scope of practice, safety standards, or appropriate referral pathways.",
  "__We believe the time has come to begin a national conversation.__",
  "We are exploring the possibility of establishing a Canadian professional association dedicated specifically to paramedical micropigmentation. Before defining its structure, mandate, membership requirements, or standards, we believe it is essential to hear from the people who will be affected by its work.",
  "We are therefore inviting practitioners, educators, healthcare professionals, surgeons, nurses, cancer-care professionals, public health representatives, insurers, legal and regulatory professionals, patient advocates, breast cancer survivors, and other interested stakeholders to participate in an open discussion about the future of this field.",
];

const LETTER_CLOSE = [
  "This consultation is not intended to predetermine the outcome. Rather, its purpose is to determine whether there is a genuine need for such an organization and, if so, what responsibilities it should carry.",
  "The findings and feedback gathered through this process will help guide the next steps, including whether to proceed with the formal establishment of an association and how that organization should be structured to serve practitioners, patients, healthcare professionals, and the public responsibly.",
  "Paramedical micropigmentation has the potential to play an important role in restorative care. Its future should be shaped collaboratively, thoughtfully, and with the highest regard for safety, education, ethics, and the people whose lives are touched by this work.",
  "We welcome interested individuals and organizations to join the conversation and help shape the future of paramedical micropigmentation in Canada.",
];

function Paragraph({ text }) {
  if (text.startsWith("__") && text.endsWith("__")) {
    return <p className="font-serif text-2xl lg:text-3xl text-ccpm-olive italic my-8 leading-snug">{text.slice(2, -2)}</p>;
  }
  return <p className="text-ccpm-charcoal/90 leading-[1.9] mb-6 text-[1.05rem] font-light">{text}</p>;
}

export default function Consultation() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", role: "", organization: "", email: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/consultation/comments").then((r) => setComments(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consultation/comments", { ...form, email: form.email || null });
      setSubmitted(true);
      setForm({ name: "", role: "", organization: "", email: "", comment: "" });
      toast.success("Thank you — your comment has been submitted for review.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your comment.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHero overline="Public Consultation — Now Open" testId="consultation-hero"
        title="Exploring the need for a Canadian Association for Paramedical Micropigmentation"
        subtitle="An open invitation to practitioners, healthcare professionals, patient advocates, survivors, and the public to help shape the future of restorative paramedical micropigmentation in Canada." />

      <Section className="py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Letter */}
          <article className="lg:col-span-7">
            <Reveal>
              <Overline>An invitation to participate</Overline>
              <div className="mt-8">
                {LETTER.map((p, i) => <Paragraph key={i} text={p} />)}
              </div>
            </Reveal>

            <Reveal>
              <div className="my-10 border border-ccpm-border bg-ccpm-stone p-8">
                <h3 className="font-serif text-2xl text-ccpm-charcoal">Areas for discussion may include</h3>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {AREAS.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-ccpm-taupe">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-ccpm-olive shrink-0" />{a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal>
              <div>{LETTER_CLOSE.map((p, i) => <Paragraph key={i} text={p} />)}</div>
              <p className="mt-8 font-serif text-xl text-ccpm-charcoal">— The Canadian Council for Paramedical Micropigmentation</p>
            </Reveal>
          </article>

          {/* Comment form */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="border border-ccpm-border bg-white p-8" id="join">
                <div className="flex items-center gap-3 mb-2">
                  <MessageSquareQuote size={22} strokeWidth={1.5} className="text-ccpm-olive" />
                  <h3 className="font-serif text-2xl text-ccpm-charcoal">Join the conversation</h3>
                </div>
                <p className="text-sm text-ccpm-taupe font-light leading-relaxed mb-6">Share your perspective. Comments are reviewed by the Council before being published to keep the discussion constructive and respectful.</p>

                {submitted ? (
                  <div className="text-center py-8" data-testid="comment-submitted">
                    <CheckCircle2 size={40} strokeWidth={1.3} className="text-ccpm-olive mx-auto" />
                    <p className="mt-4 font-serif text-2xl text-ccpm-charcoal">Thank you</p>
                    <p className="mt-2 text-sm text-ccpm-taupe font-light">Your comment has been submitted for review and will appear once approved.</p>
                    <Button variant="outline" className="mt-6" onClick={() => setSubmitted(false)} data-testid="comment-another">Add another comment</Button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="comment-form" className="space-y-4">
                    <input required placeholder="Your name *" value={form.name} onChange={set("name")} className="cons-input" data-testid="comment-name" />
                    <select value={form.role} onChange={set("role")} className="cons-input" data-testid="comment-role">
                      <option value="">I am a… (optional)</option>
                      {STAKEHOLDERS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input placeholder="Organization (optional)" value={form.organization} onChange={set("organization")} className="cons-input" data-testid="comment-org" />
                    <input type="email" placeholder="Email (optional, not published)" value={form.email} onChange={set("email")} className="cons-input" data-testid="comment-email" />
                    <textarea required rows={5} placeholder="Your comment *" value={form.comment} onChange={set("comment")} className="cons-input resize-none" data-testid="comment-text" />
                    <Button type="submit" disabled={busy} className="w-full" data-testid="comment-submit">
                      {busy ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Submit your comment"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </aside>
        </div>
      </Section>

      {/* Published comments */}
      <div className="bg-ccpm-stone py-16 lg:py-24 border-t border-ccpm-border">
        <Section>
          <Reveal>
            <Overline>The conversation so far</Overline>
            <h2 className="font-serif text-3xl lg:text-4xl text-ccpm-charcoal mt-4">{comments.length > 0 ? `${comments.length} published ${comments.length === 1 ? "comment" : "comments"}` : "Be the first to contribute"}</h2>
          </Reveal>

          {loading ? (
            <div className="flex justify-center py-16 text-ccpm-olive"><Loader2 className="animate-spin" /></div>
          ) : comments.length === 0 ? (
            <p className="mt-6 text-ccpm-taupe font-light max-w-xl">Approved comments will appear here as the consultation unfolds. Add yours above to help shape the discussion.</p>
          ) : (
            <div className="mt-10 columns-1 md:columns-2 gap-6 [column-fill:_balance]" data-testid="comments-list">
              {comments.map((c, i) => (
                <motion.figure key={c.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                  className="break-inside-avoid mb-6 bg-white border border-ccpm-border p-7" data-testid={`comment-${c.id}`}>
                  <MessageSquareQuote size={20} strokeWidth={1.5} className="text-ccpm-olive-soft" />
                  <blockquote className="mt-3 text-ccpm-charcoal/90 font-light leading-relaxed">{c.comment}</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-ccpm-border">
                    <span className="font-serif text-lg text-ccpm-charcoal">{c.name}</span>
                    {(c.role || c.organization) && <span className="block text-xs text-ccpm-taupe mt-0.5">{[c.role, c.organization].filter(Boolean).join(" · ")}</span>}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          )}
        </Section>
      </div>

      <style>{`.cons-input{width:100%;padding:0.7rem 0.85rem;background:#FDFBF7;border:1px solid #E6DFD9;font-size:0.9rem;color:#2B2B2B;outline:none;transition:border-color .2s}.cons-input:focus{border-color:#6B705C}`}</style>
    </>
  );
}
